<template>
  <div class="locations-page">
    <div v-if="!isAdmin" class="no-access">
      <p>当前账户无权限访问客户管理功能。</p>
    </div>
    <template v-else>
      <div class="header-row">
        <div>
          <h2>客户信息管理</h2>
          <p class="subtitle">共 {{ locations.length }} 个客户</p>
        </div>
        <button class="refresh-btn" :disabled="loading" @click="loadLocations">
          {{ loading ? '刷新中...' : '刷新' }}
        </button>
      </div>
      <p v-if="error" class="error">{{ error }}</p>

      <div v-if="loading && !locations.length" class="loading-state">
        正在加载客户信息...
      </div>

      <div class="location-list">
        <div
          v-for="location in locations"
          :key="location.id"
          class="location-item"
          @click="goToLocation(location.id)"
        >
          <div class="location-name">{{ location.name }}</div>
          <div class="location-meta">
            <span>ID: {{ location.id }}</span>
            <span v-if="location.password">密码: {{ location.password }}</span>
          </div>
          <div class="location-time">
            <span>创建：{{ formatDate(location.createTime) }}</span>
            <span>更新：{{ formatDate(location.updateTime) }}</span>
          </div>
          <span
            class="location-settings"
            @click.stop="openSettings(location)"
            title="设置"
          >
            <span class="material-icons">settings</span>
          </span>
        </div>

        <div class="location-item add-location" @click="showAddDialog = true">
          <div class="add-icon">+</div>
          <div class="add-text">添加新客户</div>
        </div>
      </div>

      <div v-if="showAddDialog" class="dialog-mask">
        <div class="dialog">
          <h3>添加新客户</h3>
          <input v-model="newLocation.name" placeholder="请输入客户名称" />
          <input
            v-model="newLocation.password"
            placeholder="可选：设置初始密码"
          />
          <div class="dialog-actions">
            <button :disabled="saving" @click="addLocation">
              {{ saving ? '提交中...' : '确定' }}
            </button>
            <button :disabled="saving" @click="handleCloseAddDialog">取消</button>
          </div>
        </div>
      </div>

      <div v-if="showSettingsDialog" class="dialog-mask">
        <div class="dialog">
          <h3>编辑客户</h3>
          <p class="dialog-tip">ID: {{ editLocation?.id }}</p>
          <input v-model="editLocationName" placeholder="客户名称" />
          <input
            v-model="editLocationPassword"
            placeholder="可选：更新密码"
          />
          <div class="dialog-actions">
            <button :disabled="saving" @click="saveLocationChanges">
              {{ saving ? '保存中...' : '保存' }}
            </button>
            <button :disabled="saving" class="danger" @click="deleteLocationConfirm">
              删除
            </button>
            <button :disabled="saving" @click="handleCloseEditDialog">取消</button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  fetchLocations,
  createLocation,
  updateLocation,
  deleteLocation
} from '@/api/locations';

export default {
  name: 'LocationsPage',
  data() {
    return {
      locations: [],
      loading: false,
      error: '',
      showAddDialog: false,
      newLocation: {
        name: '',
        password: ''
      },
      showSettingsDialog: false,
      editLocation: null,
      editLocationName: '',
      editLocationPassword: '',
      saving: false
    };
  },
  computed: {
    isAdmin() {
      return localStorage.getItem('userType') === 'admin';
    }
  },
  created() {
    if (this.isAdmin) {
      this.loadLocations();
    }
  },
  methods: {
    async loadLocations() {
      this.loading = true;
      this.error = '';
      try {
        const res = await fetchLocations();
        if (res.data.code === 200) {
          this.locations = res.data.data || [];
        } else {
          this.error = res.data.message || '加载客户列表失败';
        }
      } catch (err) {
        console.error('加载客户失败:', err);
        this.error = '网络错误，无法获取客户列表';
      } finally {
        this.loading = false;
      }
    },
    goToLocation(sensorId) {
      this.$router.push({ name: 'StatePage', params: { sensorId } });
    },
    async addLocation() {
      if (!this.newLocation.name.trim()) {
        alert('请输入客户名称');
        return;
      }
      this.saving = true;
      try {
        const payload = {
          name: this.newLocation.name.trim()
        };
        if (this.newLocation.password.trim()) {
          payload.password = this.newLocation.password.trim();
        }
        const res = await createLocation(payload);
        if (res.data.code === 200) {
          this.showAddDialog = false;
          this.resetAddForm();
          this.loadLocations();
        } else {
          alert(res.data.message || '添加失败');
        }
      } catch (err) {
        console.error('添加客户失败:', err);
        alert('网络错误，添加失败');
      } finally {
        this.saving = false;
      }
    },
    openSettings(location) {
      this.editLocation = { ...location };
      this.editLocationName = location.name;
      this.editLocationPassword = '';
      this.showSettingsDialog = true;
    },
    async saveLocationChanges() {
      if (!this.editLocationName.trim()) {
        alert('请输入客户名称');
        return;
      }
      this.saving = true;
      try {
        const payload = {
          id: this.editLocation.id,
          name: this.editLocationName.trim()
        };
        if (this.editLocationPassword.trim()) {
          payload.password = this.editLocationPassword.trim();
        }
        const res = await updateLocation(this.editLocation.id, payload);
        if (res.data.code === 200) {
          this.showSettingsDialog = false;
          this.resetEditForm();
          this.loadLocations();
        } else {
          alert(res.data.message || '保存失败');
        }
      } catch (err) {
        console.error('更新客户失败:', err);
        alert('网络错误，保存失败');
      } finally {
        this.saving = false;
      }
    },
    async deleteLocationConfirm() {
      if (!this.editLocation) return;
      if (!confirm(`确定删除客户「${this.editLocation.name}」吗？`)) {
        return;
      }
      this.saving = true;
      try {
        const res = await deleteLocation(this.editLocation.id);
        if (res.data.code === 200) {
          this.showSettingsDialog = false;
          this.resetEditForm();
          this.loadLocations();
        } else {
          alert(res.data.message || '删除失败');
        }
      } catch (err) {
        console.error('删除客户失败:', err);
        alert('网络错误，删除失败');
      } finally {
        this.saving = false;
      }
    },
    resetAddForm() {
      this.newLocation = { name: '', password: '' };
    },
    resetEditForm() {
      this.editLocation = null;
      this.editLocationName = '';
      this.editLocationPassword = '';
    },
    handleCloseAddDialog() {
      if (this.saving) return;
      this.showAddDialog = false;
      this.resetAddForm();
    },
    handleCloseEditDialog() {
      if (this.saving) return;
      this.showSettingsDialog = false;
      this.resetEditForm();
    },
    formatDate(value) {
      if (!value) return '--';
      try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleString('zh-CN', { hour12: false });
      } catch {
        return value;
      }
    }
  }
};
</script>

<style scoped>
.locations-page {
  padding: 20px;
  color: white;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

h2 {
  color: #00f3ff;
  margin: 0;
}

.subtitle {
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9em;
}

.refresh-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #00f3ff;
  background: transparent;
  color: #00f3ff;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-btn:not(:disabled):hover {
  background: #00f3ff;
  color: #0a1040;
}

.error {
  color: #ff6b6b;
  margin-bottom: 10px;
}

.loading-state {
  margin-bottom: 16px;
  color: rgba(255, 255, 255, 0.7);
}

.location-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.location-item {
  position: relative;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 243, 255, 0.2);
  border-radius: 12px;
  padding: 18px 20px 16px 20px;
  width: 280px;
  color: #b6eaff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.location-item:hover {
  background: rgba(0, 243, 255, 0.1);
  border-color: #00f3ff;
  box-shadow: 0 0 16px rgba(0, 243, 255, 0.2);
}

.location-item.add-location {
  justify-content: center;
  align-items: center;
  border: 2px dashed rgba(0, 243, 255, 0.4);
  background: rgba(0, 0, 0, 0.1);
}

.add-icon {
  font-size: 3em;
  color: rgba(0, 243, 255, 0.6);
}

.add-text {
  font-size: 1em;
  color: rgba(0, 243, 255, 0.6);
}

.location-name {
  font-size: 1.2em;
  font-weight: bold;
  color: #fff;
}

.location-meta,
.location-time {
  display: flex;
  justify-content: space-between;
  font-size: 0.85em;
  color: rgba(255, 255, 255, 0.7);
}

.location-settings {
  position: absolute;
  top: 8px;
  right: 10px;
  font-size: 1.2em;
  color: #00bcd4;
}

.location-settings:hover {
  color: #ff9800;
}

.dialog-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.dialog {
  background: white;
  padding: 24px;
  border-radius: 12px;
  width: 320px;
  color: #1f2a44;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}

.dialog h3 {
  color: #00bcd4;
  margin-bottom: 16px;
  text-align: center;
}

.dialog-tip {
  margin-bottom: 10px;
  font-size: 0.9em;
  color: #607d8b;
}

.dialog input {
  width: 100%;
  padding: 10px;
  margin-bottom: 14px;
  border: 1px solid #d0d7e2;
  border-radius: 6px;
  background: #f5f7fb;
  color: #1f2a44;
}

.dialog input:focus {
  border-color: #00bcd4;
  outline: none;
}

.dialog-actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.dialog button {
  flex: 1;
  padding: 10px 0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.dialog button:not(.danger) {
  background: #00bcd4;
  color: #fff;
}

.dialog button:not(.danger):hover:not(:disabled) {
  background: #0192a8;
}

.dialog button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.dialog button:last-child {
  background: #e0e5ec;
  color: #1f2a44;
}

.danger {
  background: #ff6b6b !important;
  color: #fff !important;
}

.no-access {
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 40px;
  text-align: center;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.material-icons {
  font-size: 1.3em;
  vertical-align: middle;
}
</style> 